import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LangToggle from './LangToggle'
import AuthModal from './AuthModal'
import FeedbackModal from './FeedbackModal'
import { useAuthStore } from '../store/auth'
import { api } from '../api/client'
import type { AuthResponse } from '../api/types'

export default function Nav() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const setAuth = useAuthStore((s) => s.setAuth)
  const authModalOpen = useAuthStore((s) => s.authModalOpen)
  const openAuthModal = useAuthStore((s) => s.openAuthModal)
  const closeAuthModal = useAuthStore((s) => s.closeAuthModal)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const userRef = useRef<HTMLDivElement>(null)

  // 调试模式：后端 AUTH_SKIP=true 时自动模拟登录
  useEffect(() => {
    if (user) return
    let alive = true
    api
      .authConfig()
      .then((c) => {
        if (alive && c.skip_auth) {
          setAuth({
            access_token: 'skip-auth',
            refresh_token: '',
            expires_in: 0,
            user: { id: 'debug', email: 'debug@visword.local', name: t('nav.debugMode') },
          } as unknown as AuthResponse)
        }
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [user, setAuth, t])

  // 点击外部收起用户菜单
  useEffect(() => {
    if (!userMenuOpen) return
    const onClick = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [userMenuOpen])

  return (
    <nav className="nav">
      <div className="nav-inner">
        <a href="/" className="logo" aria-label="Visword home">
          <svg className="logo-mark" width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
            <defs>
              <linearGradient id="navLogo" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                <stop stopColor="#6366f1" />
                <stop offset="1" stopColor="#a855f7" />
              </linearGradient>
            </defs>
            <rect width="64" height="64" rx="15" fill="url(#navLogo)" />
            <rect x="12" y="44" width="6" height="8" rx="3" fill="#fff" />
            <rect x="21" y="37" width="6" height="15" rx="3" fill="#fff" />
            <rect x="30" y="29" width="6" height="23" rx="3" fill="#fff" />
            <path d="M40 20 L40 52 L57 36 Z" fill="#fff" />
          </svg>
          <span className="logo-text">Visword</span>
          <span className="nav-badge">{t('nav.badge')}</span>
        </a>
        <div className="nav-right">
          <button className="btn btn-ghost btn-sm" onClick={() => setFeedbackOpen(true)} title={t('feedback.button')}>
            💬 <span className="nav-fb-text">{t('feedback.button')}</span>
          </button>
          <LangToggle />
          {user ? (
            <div className="nav-user-wrap" ref={userRef}>
              <button
                type="button"
                className={`nav-user-btn${userMenuOpen ? ' active' : ''}`}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <span className="nav-user-avatar">{(user.name || '?')[0].toUpperCase()}</span>
                <span className="nav-user-name">{user.name}</span>
                <span className="nav-user-caret">▾</span>
              </button>
              {userMenuOpen && (
                <div className="nav-user-menu">
                  <div className="nav-user-info">
                    <div className="nav-user-display">{user.name}</div>
                    <div className="nav-user-email">{user.email}</div>
                  </div>
                  <button
                    type="button"
                    className="nav-user-logout"
                    onClick={() => {
                      logout()
                      setUserMenuOpen(false)
                    }}
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="btn btn-outline btn-sm" onClick={openAuthModal}>
              {t('nav.login')}
            </button>
          )}
        </div>
      </div>
      {authModalOpen && <AuthModal onClose={closeAuthModal} />}
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </nav>
  )
}
